import React, { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Plus, Pencil, Trash2, Shirt, Search, Filter, ChevronDown, ChevronUp, MoreHorizontal } from 'lucide-react'
import { FeaturedLayout } from '../../layouts'
import {
  Button, Input, Label, Card, CardContent, Badge, Checkbox,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from '../../ui'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../../ui/dropdown-menu'
import { getPrendas, createPrenda, updatePrenda, deletePrenda, getOrgDeportes, getPlanes } from '../../../lib/supabase'
import { useTemporadaActiva } from '../pagos/useTemporadaActiva'
import { useOrganization } from '../../../contexts/OrganizationContext'
import { useConfirmDialog } from '../../../hooks/useConfirmDialog'
import { useUnsavedGuard } from '../../../hooks/useUnsavedGuard'
import { useDeportes } from '../../../hooks/useDeportes'
import { opcionesDeporte } from '../../../lib/deportesOrg'
import { formatMoney } from '../../../utils/helpers'

const DEPORTE_COLORS = {
  todos: 'bg-gray-100 text-gray-800',
  flag: 'bg-blue-100 text-blue-800',
  tackle: 'bg-red-100 text-red-800',
  equipado: 'bg-purple-100 text-purple-800',
}

const TALLAS_DEFAULT = ['XS', 'S', 'M', 'L', 'XL', 'XXL']

const initialForm = {
  nombre: '',
  deporte: 'todos',
  tallas: ['S', 'M', 'L', 'XL'],
  precio: '',
  descripcion: '',
  personalizada: false,
}

const PrendasPage = () => {
  const [searchParams] = useSearchParams()
  const { org } = useOrganization()
  const { confirm, alert, ConfirmDialog } = useConfirmDialog()
  const { temporadaId } = useTemporadaActiva()
  const [orgDeportes, setOrgDeportes] = useState([])
  const { label: nombreDeporte } = useDeportes()
  const [prendas, setPrendas] = useState([])
  const [planMap, setPlanMap] = useState({}) // concepto_id → plan nombre
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const guard = useUnsavedGuard({ onClose: () => setFormOpen(false) })
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(initialForm)
  const [saving, setSaving] = useState(false)
  // Se puede llegar aqui desde Conceptos de cobro ("Gestionado en Uniformes"), que
  // manda ?buscar=<prenda>. Sin esto aterrizabas en el catalogo completo y tenias que
  // volver a buscar lo que ya habias senalado.
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('buscar') || '')
  const [showFilters, setShowFilters] = useState(false)
  const [deporteFilterVal, setDeporteFilterVal] = useState('todos')

  const deporteOptions = [
    { value: 'todos', label: 'Todos los deportes' },
    ...opcionesDeporte(orgDeportes),
  ]

  const load = useCallback(async () => {
    setLoading(true)
    const [prendasRes, deportesRes, planesRes] = await Promise.all([
      getPrendas(),
      getOrgDeportes(org?.id),
      temporadaId ? getPlanes({ temporadaId }) : Promise.resolve({ data: [] }),
    ])
    setPrendas(prendasRes.data || [])
    setOrgDeportes(deportesRes.data || [])

    // Build map: concepto_id → plan name
    const map = {}
    ;(planesRes.data || []).forEach((plan) => {
      ;(plan.pagos_planes_items || []).forEach((item) => {
        if (item.concepto_id) map[item.concepto_id] = plan.nombre
      })
    })
    setPlanMap(map)
    setLoading(false)
  }, [org?.id, temporadaId])

  useEffect(() => { load() }, [load])

  const openCreate = () => {
    setEditing(null)
    setForm(initialForm)
    guard.resetDirty()
    setFormOpen(true)
  }

  const openEdit = (p) => {
    setEditing(p)
    setForm({
      nombre: p.nombre || '',
      deporte: p.deporte || 'todos',
      tallas: p.tallas || ['S', 'M', 'L', 'XL'],
      precio: p.precio || '',
      descripcion: p.descripcion || '',
      personalizada: p.personalizada || false,
    })
    guard.resetDirty()
    setFormOpen(true)
  }

  const toggleTalla = (talla) => {
    guard.markDirty()
    setForm((prev) => ({
      ...prev,
      tallas: prev.tallas.includes(talla)
        ? prev.tallas.filter((t) => t !== talla)
        : [...prev.tallas, talla],
    }))
  }

  const handleSave = async (e) => {
    e.preventDefault()
    if (!form.nombre.trim()) return
    if (!form.tallas.length) return
    if (!form.precio) return

    setSaving(true)
    const payload = {
      nombre: form.nombre,
      deporte: form.deporte,
      tallas: form.tallas,
      precio: Number(form.precio),
      descripcion: form.descripcion || null,
      personalizada: form.personalizada,
    }
    const { error } = editing
      ? await updatePrenda(editing.id, payload)
      : await createPrenda(payload)
    setSaving(false)
    if (error) { await alert('Error', error.message || 'No se pudo guardar la prenda'); return }
    guard.resetDirty()
    setFormOpen(false)
    load()
  }

  const handleDelete = async (p) => {
    if (!await confirm('Eliminar prenda', `Eliminar "${p.nombre}"?`, { destructive: true })) return
    const { pendientes } = await deletePrenda(p.id)
    if (pendientes > 0) {
      await alert('Prenda eliminada', `Habia ${pendientes} entregas pendientes que quedaran en historial.`)
    }
    load()
  }

  const filtered = prendas.filter((p) => {
    const matchSearch = !searchTerm || p.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) || p.deporte?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchDeporte = deporteFilterVal === 'todos' || p.deporte === deporteFilterVal || p.deporte === 'todos'
    return matchSearch && matchDeporte
  })

  const hasActiveFilters = deporteFilterVal !== 'todos'

  return (
    <FeaturedLayout
      title="Prendas y Uniformes"
      subtitle="Catalogo de prendas por deporte"
      refreshAction={load}
    >
      <div className="mb-4 flex items-center gap-2 flex-nowrap">
        <div className="flex-1 min-w-0 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Buscar prenda..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <span className="text-sm text-muted-foreground hidden sm:block">
          <span className="font-medium text-foreground">{filtered.length}</span> prendas
        </span>
        <Button variant="outline" onClick={() => setShowFilters(!showFilters)} className="relative h-10 w-10 p-0 sm:h-10 sm:w-auto sm:px-3">
          <Filter className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Filtros</span>
          {showFilters ? <ChevronUp className="h-4 w-4 ml-2 hidden sm:inline" /> : <ChevronDown className="h-4 w-4 ml-2 hidden sm:inline" />}
        </Button>
        <Button onClick={openCreate} className="h-10 shrink-0" aria-label="Nueva prenda" title="Nueva prenda">
          <Plus className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Nueva prenda</span>
        </Button>
      </div>

      {showFilters && (
        <div className="mb-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-foreground">Deporte</Label>
              <Select value={deporteFilterVal} onValueChange={setDeporteFilterVal}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {deporteOptions.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {hasActiveFilters && (
            <div className="flex justify-end">
              <Button variant="outline" size="sm" onClick={() => setDeporteFilterVal('todos')}>
                Limpiar filtros
              </Button>
            </div>
          )}
        </div>
      )}

      {loading ? (
        <p className="text-muted-foreground text-sm py-8 text-center">Cargando...</p>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Shirt className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-30" />
            <p className="text-muted-foreground mb-4">No hay prendas registradas</p>
            <Button onClick={openCreate}><Plus className="h-4 w-4 mr-2" /> Crear primera prenda</Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          {/* Movil: lista. En tabla, las tallas son una lista de badges dentro de una
              columna angosta: se apilaban una por linea y estiraban la fila, ademas de
              empujar las acciones fuera de la pantalla. */}
          <div className="sm:hidden divide-y px-4">
            {filtered.map((p) => (
              <div key={p.id} className="flex items-start justify-between gap-3 py-3">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm truncate">{p.nombre}</p>
                  {p.descripcion && (
                    <p className="text-xs text-muted-foreground truncate">{p.descripcion}</p>
                  )}
                  <p className="text-xs text-muted-foreground mt-0.5">
                    <span>{nombreDeporte(p.deporte)}</span>
                    {(p.tallas || []).length > 0 && ` · ${(p.tallas || []).join(' · ')}`}
                  </p>
                  {p.personalizada && <Badge variant="outline" className="text-[10px] mt-1 mr-1">Número y nickname</Badge>}
                  {p.concepto_id && planMap[p.concepto_id] && (
                    <Badge variant="secondary" className="text-[10px] mt-1">{planMap[p.concepto_id]}</Badge>
                  )}
                </div>
                <div className="flex items-start gap-1 shrink-0">
                  <span className="text-sm font-semibold tabular-nums">{formatMoney(p.precio)}</span>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0 -mt-0.5" title="Más acciones">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEdit(p)}>
                        <Pencil className="h-4 w-4" />
                        Editar prenda
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleDelete(p)}
                        className="text-destructive focus:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                        Eliminar prenda
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))}
          </div>

          <Table className="hidden sm:table">
            <TableHeader>
              <TableRow>
                <TableHead>Prenda</TableHead>
                <TableHead>Deporte</TableHead>
                <TableHead>Tallas</TableHead>
                <TableHead>Precio</TableHead>
                <TableHead className="hidden md:table-cell">Plan</TableHead>
                <TableHead className="w-20"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((p) => (
                <TableRow key={p.id}>
                  <TableCell>
                    <div>
                      <span className="font-medium">{p.nombre}</span>
                      {p.descripcion && <p className="text-xs text-muted-foreground">{p.descripcion}</p>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      <Badge className={`${DEPORTE_COLORS[p.deporte] || DEPORTE_COLORS.todos} text-xs`}>
                        {nombreDeporte(p.deporte)}
                      </Badge>
                      {p.personalizada && <Badge variant="outline" className="text-xs">Número y nickname</Badge>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(p.tallas || []).map((t) => (
                        <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="font-medium">{formatMoney(p.precio)}</TableCell>
                  <TableCell className="hidden md:table-cell text-xs">
                    {p.concepto_id && planMap[p.concepto_id] ? (
                      <Badge variant="secondary" className="text-xs">{planMap[p.concepto_id]}</Badge>
                    ) : (
                      <span className="text-muted-foreground">Sin plan</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1 justify-end">
                      <Button variant="outline" size="sm" className="h-7 px-2" onClick={() => openEdit(p)}>
                        <Pencil className="h-3.5 w-3.5 mr-1.5" />
                        <span className="text-xs">Editar</span>
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Más acciones">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => handleDelete(p)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                            Eliminar prenda
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Form Modal */}
      <Dialog open={formOpen} onOpenChange={guard.onOpenChange}>
        <DialogContent {...guard.dirtyProps} className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar Prenda' : 'Nueva Prenda'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre *</Label>
              <Input value={form.nombre} onChange={(e) => setForm((p) => ({ ...p, nombre: e.target.value }))} placeholder="Ej: Jersey Local" required />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Deporte</Label>
                <Select value={form.deporte} onValueChange={(v) => { guard.markDirty(); setForm((p) => ({ ...p, deporte: v })) }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos los deportes</SelectItem>
                    {opcionesDeporte(orgDeportes, form.deporte === 'todos' ? null : form.deporte, nombreDeporte).map((o) => (
                      <SelectItem key={o.value} value={o.value} disabled={o.deshabilitado}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Precio *</Label>
                <Input type="number" min="0" step="0.01" value={form.precio} onChange={(e) => setForm((p) => ({ ...p, precio: e.target.value }))} placeholder="$0.00" required />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Tallas disponibles *</Label>
              <div className="flex flex-wrap gap-2">
                {TALLAS_DEFAULT.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => toggleTalla(t)}
                    className={`px-3 py-1.5 rounded-md border text-sm font-medium transition-colors ${
                      form.tallas.includes(t)
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background text-muted-foreground border-input hover:bg-muted'
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
              {form.tallas.length === 0 && <p className="text-xs text-destructive">Selecciona al menos una talla</p>}
            </div>
            <div className="flex items-start gap-2">
              <Checkbox
                id="personalizada"
                checked={form.personalizada}
                onCheckedChange={(v) => { guard.markDirty(); setForm((p) => ({ ...p, personalizada: !!v })) }}
              />
              <div className="space-y-0.5">
                <Label htmlFor="personalizada">Lleva número y nickname</Label>
                <p className="text-xs text-muted-foreground">
                  La pieza es de un jugador en concreto: al recibirla del proveedor se marca a nombre
                  de quién. Sin esto, la prenda es intercambiable y se recibe por cantidad.
                </p>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Descripción</Label>
              <Input value={form.descripcion} onChange={(e) => setForm((p) => ({ ...p, descripcion: e.target.value }))} placeholder="Opcional" />
            </div>
            {!editing && (
              <p className="text-xs text-muted-foreground bg-muted/50 p-2 rounded">
                Al crear la prenda se generara automaticamente un concepto de pago tipo "uniforme" vinculado.
              </p>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => guard.onOpenChange(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving || !form.nombre.trim() || !form.tallas.length || !form.precio}>
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </form>
          {guard.DiscardDialog}
        </DialogContent>
      </Dialog>
      <ConfirmDialog />
    </FeaturedLayout>
  )
}

export default PrendasPage
